import { useQuery } from '@tanstack/react-query';

import {
  EXCLUSION_LAYER_OPTIONS,
  INITIAL_REGISTRY_OPTIONS,
  RANGELAND,
  STRATIFY,
} from 'constants/projects';

import { usePageTitle } from 'util/hooks/usePageTitle';
import { carbonProjectQueryOptions, carbonSamplePlanConfigQueryOptions } from 'util/queryOptions';
import CenteredLoader from 'common/CenteredLoader';

import { useProjectRouteParams } from '../hooks';

import CreateSamplePlanForm from './CreateSamplePlanForm';
import { useProjectsTranslations } from './hooks';
import { SamplePlanFormProvider, useSamplePlanForm } from './samplePlanFormContextV2';
import {
  getMeterCoreFormValues,
  getSoilHealthFormValues,
  getThirtyCmCoreFormValues,
  getWetAggregateStabilityFormValues,
} from './utils';

const CreateSamplePlanContainer = () => {
  const translations = useProjectsTranslations();
  const { projectId } = useProjectRouteParams();

  const { isPending: isProjectPending, data: project } = useQuery(
    carbonProjectQueryOptions(projectId),
  );
  const pageTitle = translations.createSamplingPlan;

  const { isPending: isConfigPending, data: samplePlanConfig } = useQuery(
    carbonSamplePlanConfigQueryOptions(INITIAL_REGISTRY_OPTIONS),
  );

  usePageTitle(pageTitle);

  const samplePlanForm = useSamplePlanForm({
    mode: 'uncontrolled',
    initialValues: {
      registry_list: INITIAL_REGISTRY_OPTIONS,
      project_type: RANGELAND,
      isMeterCore: true,
      isThirtyCmCore: true,
      isSoilHealth: false,
      isWetAggregateStability: false,
      exclusion_layers: EXCLUSION_LAYER_OPTIONS,
      tree_shrub: 85,
      slope: 58,
      method: STRATIFY,
      mc_dict: getMeterCoreFormValues(samplePlanConfig?.meter_core),
      step_probe_dict: getThirtyCmCoreFormValues(samplePlanConfig?.thirty_cm_core),
      sh_dict: getSoilHealthFormValues(samplePlanConfig?.soil_health),
      was_dict: getWetAggregateStabilityFormValues(samplePlanConfig?.wet_aggregate_stability),
    },
  });

  if (!samplePlanConfig || !project || isConfigPending || isProjectPending) {
    return <CenteredLoader />;
  }

  return (
    <SamplePlanFormProvider form={samplePlanForm}>
      <CreateSamplePlanForm project={project} samplePlanConfig={samplePlanConfig} />
    </SamplePlanFormProvider>
  );
};

export default CreateSamplePlanContainer;
